import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Mail, Send, CheckCircle, XCircle, AlertTriangle, Clock, 
  Gavel, Shield, User, Calendar, FileText, Eye, Upload, Trash2,
  ArrowLeft, Image, Building2, Database, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Court Criminal Record Verification
// Based on: Name, DOB, Father Name, Address (1 or all based on package)
// Verify for last 7 years, No adverse records found OR Discrepancy with admin review

const CRIMINAL_VERIFICATION_FIELDS = [
  { id: 'candidate_name', label: 'Candidate Name', required: true },
  { id: 'dob', label: 'Date of Birth', required: true },
  { id: 'father_name', label: "Father's Name", required: true },
  { id: 'address_verified', label: 'Address Verified', required: true },
  { id: 'court_name', label: 'Court Name', required: true },
  { id: 'court_type', label: 'Court Type (District/Sessions/High Court)', required: false },
  { id: 'case_status', label: 'Case Status', required: true },
  { id: 'verification_period', label: 'Verification Period (Years)', required: true, default: '7' },
  { id: 'records_found', label: 'Criminal Records Found', required: true },
  { id: 'case_details', label: 'Case Details (if any)', required: false }
];

const NOT_VERIFIED_REASONS = [
  'Court Records Unavailable',
  'Name Mismatch in Records',
  'Address Not Matching',
  'System Under Maintenance',
  'Incomplete Data Provided',
  'Other'
];

const DISCREPANCY_REASONS = [
  'Criminal Case Found - Pending',
  'Criminal Case Found - Convicted',
  'Criminal Case Found - Acquitted',
  'Civil Case Found',
  'Cheque Bounce Case Found',
  'Multiple Cases Found',
  'Name Appears in Watchlist',
  'Other'
];

export default function CourtCriminalVerificationForm({ 
  caseId, 
  checkId, 
  checkData, 
  employeeData,
  documents = [],
  onBack,
  onComplete 
}) {
  const [loading, setLoading] = useState(false);
  const [showSendToVendor, setShowSendToVendor] = useState(false);
  const [showRecordResponse, setShowRecordResponse] = useState(false);
  const [showDiscrepancyReview, setShowDiscrepancyReview] = useState(false);
  const [verificationData, setVerificationData] = useState(checkData?.criminal_verification || {});
  const [vendors, setVendors] = useState([]);
  const [addresses, setAddresses] = useState([]);
  
  // Annexure state
  const [annexures, setAnnexures] = useState([]);
  const [showUploadAnnexure, setShowUploadAnnexure] = useState(false);
  const [uploadingAnnexure, setUploadingAnnexure] = useState(false);
  const [annexureForm, setAnnexureForm] = useState({
    file_name: '',
    file_url: '',
    file_type: 'document',
    description: '',
    annexure_type: 'court_record'
  });
  
  // Vendor email form state
  const [vendorForm, setVendorForm] = useState({
    vendor_id: '',
    vendor_name: '',
    vendor_email: '',
    selected_addresses: [],
    additional_instructions: ''
  });
  
  // Response form state
  const [responseForm, setResponseForm] = useState({
    verifier_name: '',
    verification_date: '',
    verification_period_years: '7',
    overall_status: 'clear',
    overall_remarks: '',
    agent_remarks: '',
    addresses_verified: [],
    fields: CRIMINAL_VERIFICATION_FIELDS.map(f => ({
      field_name: f.label,
      candidate_value: '',
      status: 'verified',
      reason: '',
      verified_value: '',
      remarks: ''
    }))
  });

  // Discrepancy review state
  const [discrepancyReview, setDiscrepancyReview] = useState({
    admin_decision: '',
    admin_remarks: '',
    update_to_verified: false
  });

  useEffect(() => {
    fetchData();
  }, [caseId, checkId]);

  const fetchData = async () => {
    try {
      const [annexuresRes, vendorsRes] = await Promise.all([
        axios.get(`/cases/${caseId}/checks/${checkId}/annexures`),
        axios.get('/vendors').catch(() => ({ data: [] }))
      ]);
      setAnnexures(annexuresRes.data || []);
      setVendors(vendorsRes.data || []);
      
      // Get addresses from employee data
      if (employeeData) {
        const addrs = [];
        if (employeeData.present_address) {
          addrs.push({ type: 'Present', ...employeeData.present_address });
        }
        if (employeeData.permanent_address) {
          addrs.push({ type: 'Permanent', ...employeeData.permanent_address });
        }
        setAddresses(addrs);
      }
    } catch (e) {
      console.error('Failed to fetch data');
    }
  };

  useEffect(() => {
    if (employeeData) {
      const updatedFields = CRIMINAL_VERIFICATION_FIELDS.map(f => {
        let candidateValue = '';
        switch(f.id) {
          case 'candidate_name': candidateValue = employeeData.name || ''; break;
          case 'dob': candidateValue = employeeData.dob || ''; break;
          case 'father_name': candidateValue = employeeData.father_name || ''; break;
          case 'verification_period': candidateValue = '7'; break;
          default: candidateValue = '';
        }
        return {
          field_name: f.label,
          candidate_value: candidateValue,
          status: 'verified',
          reason: '',
          verified_value: '',
          remarks: ''
        };
      });
      setResponseForm(prev => ({ ...prev, fields: updatedFields }));
    }
  }, [employeeData]);

  const handleUploadAnnexure = async () => {
    if (!annexureForm.file_url) {
      toast.error('Please provide file URL');
      return;
    }
    
    setUploadingAnnexure(true);
    try {
      const res = await axios.post(`/cases/${caseId}/checks/${checkId}/annexures`, {
        case_id: caseId,
        check_id: checkId,
        ...annexureForm
      });
      setAnnexures(prev => [...prev, res.data]);
      setShowUploadAnnexure(false);
      setAnnexureForm({
        file_name: '',
        file_url: '',
        file_type: 'document',
        description: '',
        annexure_type: 'court_record'
      });
      toast.success('Annexure uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload');
    } finally {
      setUploadingAnnexure(false);
    }
  };

  const handleDeleteAnnexure = async (annexureId) => {
    if (!confirm('Delete this annexure?')) return;
    try {
      await axios.delete(`/annexures/${annexureId}`);
      setAnnexures(prev => prev.filter(a => a.id !== annexureId));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleSendToVendor = async () => {
    if (!vendorForm.vendor_email) {
      toast.error('Please enter vendor email');
      return;
    }
    if (vendorForm.selected_addresses.length === 0) {
      toast.error('Please select at least one address to verify');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`/cases/${caseId}/checks/${checkId}/send-criminal-verification`, {
        ...vendorForm,
        employee_name: employeeData?.name,
        dob: employeeData?.dob,
        father_name: employeeData?.father_name,
        addresses: addresses.filter((_, i) => vendorForm.selected_addresses.includes(i))
      });
      toast.success('Verification request sent to vendor (MOCKED)');
      setVerificationData(prev => ({
        ...prev,
        initiated_at: res.data.initiated_at,
        vendor_assigned: true,
        vendor_name: vendorForm.vendor_name
      }));
      setShowSendToVendor(false);
    } catch (error) {
      toast.error('Failed to send');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordResponse = async () => {
    if (!responseForm.verifier_name) {
      toast.error('Please enter verifier name');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.put(`/cases/${caseId}/checks/${checkId}/record-criminal-verification`, {
        ...responseForm,
        check_type: 'criminal_court_check'
      });
      
      if (res.data.overall_result === 'red') {
        // Discrepancy found - show admin review option
        toast.warning('Discrepancy found - Sending to admin for review');
        setShowRecordResponse(false);
        setShowDiscrepancyReview(true);
      } else {
        toast.success(`Verification recorded - Result: ${res.data.overall_result.toUpperCase()}`);
        setVerificationData(prev => ({ ...prev, ...res.data }));
        setShowRecordResponse(false);
        if (onComplete) onComplete(res.data);
      }
    } catch (error) {
      toast.error('Failed to record');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminReview = async () => {
    setLoading(true);
    try {
      await axios.post(`/cases/${caseId}/checks/${checkId}/discrepancy-review`, {
        admin_decision: discrepancyReview.admin_decision,
        admin_remarks: discrepancyReview.admin_remarks,
        update_status: discrepancyReview.update_to_verified
      });
      toast.success('Discrepancy review submitted');
      setShowDiscrepancyReview(false);
      if (onComplete) onComplete();
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const updateFieldStatus = (index, field, value) => {
    setResponseForm(prev => {
      const newFields = [...prev.fields];
      newFields[index] = { ...newFields[index], [field]: value };
      return { ...prev, fields: newFields };
    });
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'verified': return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case 'not_verified': return <AlertTriangle className="w-5 h-5 text-amber-600" />;
      case 'discrepancy': return <XCircle className="w-5 h-5 text-rose-600" />;
      default: return null;
    }
  };

  const getResultBadge = (result) => {
    switch(result) {
      case 'green': return <Badge className="bg-emerald-100 text-emerald-700">GREEN - No Records Found</Badge>;
      case 'amber': return <Badge className="bg-amber-100 text-amber-700">AMBER - Unable to Verify</Badge>;
      case 'red': return <Badge className="bg-rose-100 text-rose-700">RED - Discrepancy Found</Badge>;
      default: return <Badge variant="outline">Pending</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Gavel className="w-6 h-6 text-rose-600" />
              Court Criminal Record Verification
            </h2>
            <p className="text-slate-600">Case ID: {caseId} | Check ID: {checkId}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getResultBadge(verificationData?.overall_result)}
        </div>
      </div>

      {/* Verification Info */}
      <Card className="border-rose-200 bg-rose-50">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <Clock className="w-6 h-6 text-rose-600" />
            <div>
              <p className="font-medium text-rose-900">Criminal Record Verification</p>
              <p className="text-sm text-rose-700">
                Verifying criminal records for the <strong>last 7 years</strong>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Candidate Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-rose-600" />
            Candidate Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-500">Name</Label>
              <p className="font-medium">{employeeData?.name || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Date of Birth</Label>
              <p className="font-medium">{employeeData?.dob || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Father&apos;s Name</Label>
              <p className="font-medium">{employeeData?.father_name || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-slate-500">PAN Number</Label>
              <p className="font-medium">{employeeData?.pan_number || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Addresses to Verify */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-rose-600" />
            Addresses for Court Record Verification
          </CardTitle>
        </CardHeader>
        <CardContent>
          {addresses.length === 0 ? (
            <p className="text-slate-500">No addresses available</p>
          ) : (
            <div className="space-y-3">
              {addresses.map((addr, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg border">
                  <Badge className="mb-2">{addr.type} Address</Badge>
                  <p className="font-medium">
                    {addr.address_line || addr.full_address || 'N/A'}
                  </p>
                  <p className="text-sm text-slate-600">
                    {addr.city}, {addr.state} - {addr.pincode}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Annexures */}
      <Card className="border-rose-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-rose-600" />
            Annexures / Court Records
          </CardTitle>
          <Button onClick={() => setShowUploadAnnexure(true)} size="sm" className="bg-rose-600 hover:bg-rose-700">
            <Upload className="w-4 h-4 mr-2" />
            Upload Annexure
          </Button>
        </CardHeader>
        <CardContent>
          {annexures.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No annexures uploaded</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {annexures.map((annexure, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-slate-50 relative group">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-rose-600 h-6 w-6 p-0"
                    onClick={() => handleDeleteAnnexure(annexure.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-rose-600" />
                    <Badge variant="outline" className="text-xs">{annexure.annexure_type}</Badge>
                  </div>
                  <p className="text-sm font-medium truncate">{annexure.file_name || 'Document'}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2 text-xs h-7"
                    onClick={() => window.open(annexure.file_url, '_blank')}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4">
        <Button onClick={() => setShowSendToVendor(true)} className="bg-blue-600 hover:bg-blue-700">
          <Mail className="w-4 h-4 mr-2" />
          Send to Vendor
        </Button>
        <Button onClick={() => setShowRecordResponse(true)} className="bg-emerald-600 hover:bg-emerald-700">
          <CheckCircle className="w-4 h-4 mr-2" />
          Record Verification Response
        </Button>
      </div>

      {/* Verification Results */}
      {verificationData?.overall_result && (
        <Card className={
          verificationData.overall_result === 'green' ? 'border-emerald-200 bg-emerald-50' :
          verificationData.overall_result === 'red' ? 'border-rose-200 bg-rose-50' :
          'border-amber-200 bg-amber-50'
        }>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Verification Result</span>
              {getResultBadge(verificationData.overall_result)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-slate-500">Verified By</Label>
                  <p className="font-medium">{verificationData.verifier_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Verification Date</Label>
                  <p className="font-medium">{verificationData.verification_date || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Period Checked</Label>
                  <p className="font-medium">{verificationData.verification_period_years || '7'} Years</p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <p className="font-medium capitalize">{verificationData.overall_status || 'N/A'}</p>
                </div>
              </div>
              {verificationData.overall_result === 'green' && (
                <div className="p-4 bg-emerald-100 rounded-lg">
                  <p className="font-medium text-emerald-800">
                    ✓ Verified for Criminal Records for last {verificationData.verification_period_years || 7} Years - No adverse records found
                  </p>
                </div>
              )}
              {verificationData.overall_result === 'red' && (
                <div className="p-4 bg-rose-100 rounded-lg">
                  <p className="font-medium text-rose-800">
                    ✗ Discrepancy Found - {verificationData.discrepancy_details || 'Review required'}
                  </p>
                </div>
              )}
              {verificationData.agent_remarks && (
                <div>
                  <Label className="text-slate-500">Agent Remarks</Label>
                  <p>{verificationData.agent_remarks}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Send to Vendor Dialog */}
      <Dialog open={showSendToVendor} onOpenChange={setShowSendToVendor}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Send Criminal Record Verification to Vendor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-rose-50 rounded-lg border border-rose-200">
              <p className="text-sm text-rose-700">
                <strong>Note:</strong> Court records will be checked for the last 7 years for all selected addresses.
              </p>
            </div>

            {/* Address Selection */}
            <div>
              <Label className="font-medium">Select Addresses to Verify *</Label>
              <div className="space-y-2 mt-2">
                {addresses.map((addr, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 bg-slate-50 rounded">
                    <input 
                      type="checkbox"
                      id={`addr-${idx}`}
                      checked={vendorForm.selected_addresses.includes(idx)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setVendorForm(prev => ({
                            ...prev,
                            selected_addresses: [...prev.selected_addresses, idx]
                          }));
                        } else {
                          setVendorForm(prev => ({
                            ...prev,
                            selected_addresses: prev.selected_addresses.filter(i => i !== idx)
                          }));
                        }
                      }}
                      className="mt-1"
                    />
                    <label htmlFor={`addr-${idx}`} className="text-sm cursor-pointer">
                      <Badge className="mr-2">{addr.type}</Badge>
                      {addr.address_line || addr.full_address}, {addr.city}, {addr.state}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {vendors.length > 0 && (
              <div>
                <Label>Select Vendor</Label>
                <Select 
                  value={vendorForm.vendor_id}
                  onValueChange={(v) => {
                    const vendor = vendors.find(vd => vd.id === v);
                    setVendorForm(prev => ({
                      ...prev,
                      vendor_id: v,
                      vendor_name: vendor?.name || '',
                      vendor_email: vendor?.email || ''
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Vendor Name *</Label>
              <Input 
                value={vendorForm.vendor_name}
                onChange={(e) => setVendorForm(prev => ({ ...prev, vendor_name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Vendor Email *</Label>
              <Input 
                type="email"
                value={vendorForm.vendor_email}
                onChange={(e) => setVendorForm(prev => ({ ...prev, vendor_email: e.target.value }))}
              />
            </div>
            <div>
              <Label>Additional Instructions</Label>
              <Textarea 
                value={vendorForm.additional_instructions}
                onChange={(e) => setVendorForm(prev => ({ ...prev, additional_instructions: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendToVendor(false)}>Cancel</Button>
            <Button onClick={handleSendToVendor} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Sending...' : 'Send to Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Response Dialog */}
      <Dialog open={showRecordResponse} onOpenChange={setShowRecordResponse}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Record Criminal Record Verification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Verifier Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>Verifier Name *</Label>
                <Input 
                  value={responseForm.verifier_name}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verifier_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Verification Date *</Label>
                <Input 
                  type="date"
                  value={responseForm.verification_date}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verification_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Overall Status */}
            <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
              <Label className="font-medium">Verification Result *</Label>
              <RadioGroup 
                value={responseForm.overall_status}
                onValueChange={(v) => setResponseForm(prev => ({ ...prev, overall_status: v }))}
                className="mt-3 space-y-2"
              >
                <div className="flex items-center space-x-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <RadioGroupItem value="clear" id="clear" />
                  <Label htmlFor="clear" className="cursor-pointer flex-1">
                    <span className="font-medium text-emerald-700">No Criminal Records Found (GREEN)</span>
                    <p className="text-sm text-emerald-600">
                      Verified for Criminal Records for last 7 Years - No adverse records found
                    </p>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <RadioGroupItem value="unable_to_verify" id="unable" />
                  <Label htmlFor="unable" className="cursor-pointer flex-1">
                    <span className="font-medium text-amber-700">Unable to Verify (AMBER)</span>
                    <p className="text-sm text-amber-600">
                      Court records unavailable or incomplete data
                    </p>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-rose-50 rounded-lg border border-rose-200">
                  <RadioGroupItem value="discrepancy" id="discrepancy" />
                  <Label htmlFor="discrepancy" className="cursor-pointer flex-1">
                    <span className="font-medium text-rose-700">Discrepancy Found (RED)</span>
                    <p className="text-sm text-rose-600">
                      Criminal/Civil case found - Will be sent to admin for review
                    </p>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Discrepancy Details (if RED) */}
            {responseForm.overall_status === 'discrepancy' && (
              <div className="p-4 bg-rose-100 rounded-lg border border-rose-300">
                <Label className="font-medium text-rose-800">Discrepancy Details</Label>
                <Select 
                  value={responseForm.discrepancy_reason}
                  onValueChange={(v) => setResponseForm(prev => ({ ...prev, discrepancy_reason: v }))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select discrepancy type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCREPANCY_REASONS.map((r, i) => (
                      <SelectItem key={i} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea 
                  className="mt-2"
                  placeholder="Enter case details..."
                  value={responseForm.discrepancy_details}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, discrepancy_details: e.target.value }))}
                  rows={3}
                />
              </div>
            )}

            {/* Agent Remarks */}
            <div>
              <Label>Agent Remarks</Label>
              <Textarea 
                value={responseForm.agent_remarks}
                onChange={(e) => setResponseForm(prev => ({ ...prev, agent_remarks: e.target.value }))}
                placeholder="Any additional remarks from the verification agent..."
                rows={3}
              />
            </div>

            {/* Overall Remarks */}
            <div>
              <Label>Overall Verification Remarks</Label>
              <Textarea 
                value={responseForm.overall_remarks}
                onChange={(e) => setResponseForm(prev => ({ ...prev, overall_remarks: e.target.value }))}
                placeholder="Summary of verification findings..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordResponse(false)}>Cancel</Button>
            <Button onClick={handleRecordResponse} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Verification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discrepancy Admin Review Dialog */}
      <Dialog open={showDiscrepancyReview} onOpenChange={setShowDiscrepancyReview}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" />
              Discrepancy - Admin Review Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
              <p className="text-rose-800">
                A discrepancy has been found in the criminal record verification. 
                This case has been flagged for admin review.
              </p>
            </div>

            <div>
              <Label>Admin Decision</Label>
              <Select 
                value={discrepancyReview.admin_decision}
                onValueChange={(v) => setDiscrepancyReview(prev => ({ ...prev, admin_decision: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select decision" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approve_with_remarks">Approve with Remarks</SelectItem>
                  <SelectItem value="reject">Reject Candidate</SelectItem>
                  <SelectItem value="request_more_info">Request More Information</SelectItem>
                  <SelectItem value="override_to_green">Override to GREEN (Admin Override)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Admin Remarks</Label>
              <Textarea 
                value={discrepancyReview.admin_remarks}
                onChange={(e) => setDiscrepancyReview(prev => ({ ...prev, admin_remarks: e.target.value }))}
                placeholder="Enter admin decision remarks..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="update-verified"
                checked={discrepancyReview.update_to_verified}
                onChange={(e) => setDiscrepancyReview(prev => ({ ...prev, update_to_verified: e.target.checked }))}
              />
              <Label htmlFor="update-verified" className="cursor-pointer">
                Update status to Verified (with admin remarks)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscrepancyReview(false)}>Cancel</Button>
            <Button onClick={handleAdminReview} disabled={loading} className="bg-rose-600 hover:bg-rose-700">
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Submitting...' : 'Submit Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Annexure Dialog */}
      <Dialog open={showUploadAnnexure} onOpenChange={setShowUploadAnnexure}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-rose-600" />
              Upload Court Record / Annexure
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Document Type *</Label>
              <Select 
                value={annexureForm.annexure_type}
                onValueChange={(v) => setAnnexureForm(prev => ({ ...prev, annexure_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="court_record">Court Record</SelectItem>
                  <SelectItem value="fir_copy">FIR Copy</SelectItem>
                  <SelectItem value="chargesheet">Chargesheet</SelectItem>
                  <SelectItem value="court_order">Court Order</SelectItem>
                  <SelectItem value="clearance_certificate">Clearance Certificate</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File Name</Label>
              <Input 
                value={annexureForm.file_name}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, file_name: e.target.value }))}
                placeholder="e.g., Court_Clearance_Certificate"
              />
            </div>
            <div>
              <Label>File URL *</Label>
              <Input 
                value={annexureForm.file_url}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, file_url: e.target.value }))}
                placeholder="https://example.com/document.pdf"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={annexureForm.description}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadAnnexure(false)}>Cancel</Button>
            <Button 
              onClick={handleUploadAnnexure} 
              disabled={uploadingAnnexure || !annexureForm.file_url} 
              className="bg-rose-600 hover:bg-rose-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadingAnnexure ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
